/**
 * 개요 페이지 — 현장 담당자(ContactsBar) 바로 아래의 "관련 사이트" 위젯.
 *
 * 운영 시나리오: 한 현장에 연관된 외부 사이트(관제 페이지, 모니터링 대시보드,
 * 시공사 문서함 등)를 운영자가 바로 등록하고 클릭으로 이동. 필드는 운영팀
 * 요청대로 2개 — 사이트 이름 / 링크 주소.
 *
 * 권한: 조회는 누구나, 추가/삭제는 canEdit(admin/editor). URL 스킴 보정은
 * 백엔드가 처리하므로 응답의 url 은 항상 절대 경로 → 새 탭으로 안전하게 오픈.
 */
import React, { useEffect, useState } from 'react';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Link as LinkIcon,
  AddCircleOutline as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { SiteLink, SiteLinkService } from '../../../services/SiteLinkService';
import usePermissions from '../../../hooks/usePermissions';
import { colorFromSeed } from '../../../utils/colorFromSeed';

// 링크 URL → 해당 사이트 favicon 주소. 파싱 실패 시 undefined (이니셜 fallback).
// 폐쇄망에서도 관련 사이트가 같은 LAN 이면 favicon 이 로드되고, 아니면 MUI Avatar 가
// 자동으로 children(이니셜)을 표시한다 → "로고 또는 색상" 자동 부여.
function faviconUrl(rawUrl: string): string | undefined {
  try {
    return `${new URL(rawUrl).origin}/favicon.ico`;
  } catch {
    return undefined;
  }
}

function initialOf(name: string): string {
  const s = (name || '').trim();
  return s ? s.charAt(0).toUpperCase() : '?';
}

interface Props {
  projectId: number;
}

const SiteLinksBar: React.FC<Props> = ({ projectId }) => {
  const { canEdit } = usePermissions();
  const [links, setLinks] = useState<SiteLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 인라인 추가 폼 상태
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    SiteLinkService.list(projectId)
      .then((data) => {
        if (!cancelled) setLinks(data);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '사이트 링크 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const resetForm = () => {
    setName('');
    setUrl('');
    setAdding(false);
  };

  const handleAdd = async () => {
    const trimmedName = name.trim();
    const trimmedUrl = url.trim();
    if (!trimmedName || !trimmedUrl) {
      setError('사이트 이름과 링크 주소를 모두 입력하세요.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await SiteLinkService.create(projectId, { name: trimmedName, url: trimmedUrl });
      setLinks((prev) => [...prev, created]);
      resetForm();
    } catch (e) {
      setError(e instanceof Error ? e.message : '사이트 링크 등록 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (link: SiteLink) => {
    // 낙관적 제거 후 실패 시 롤백.
    const snapshot = links;
    setLinks((prev) => prev.filter((l) => l.id !== link.id));
    try {
      await SiteLinkService.delete(link.id);
    } catch (e) {
      setLinks(snapshot);
      setError(e instanceof Error ? e.message : '사이트 링크 삭제 실패');
    }
  };

  return (
    // 외부 Paper 는 호출 측(OverviewSection)이 제공한다. 여기서는 내용만.
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: links.length === 0 && !adding ? 0 : 1.25, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <LinkIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" fontWeight={700}>
            관련 사이트
          </Typography>
          {!loading && (
            <Typography variant="caption" color="text.secondary">
              ({links.length}개)
            </Typography>
          )}
          {loading && <CircularProgress size={14} />}
        </Stack>
        {canEdit && !adding && (
          <Tooltip title="사이트 링크 추가">
            <IconButton size="small" onClick={() => setAdding(true)} aria-label="사이트 링크 추가">
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {error && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
          {error}
        </Typography>
      )}

      {/* 인라인 추가 폼 */}
      {adding && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: links.length === 0 ? 0 : 1.5 }}
        >
          <TextField
            size="small"
            label="사이트 이름"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            sx={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            label="링크 주소"
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            disabled={saving}
            sx={{ flex: 1, minWidth: 200 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAdd();
            }}
          />
          <Stack direction="row" spacing={0.5}>
            <Button
              size="small"
              variant="contained"
              onClick={handleAdd}
              disabled={saving}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              {saving ? '저장 중…' : '추가'}
            </Button>
            <IconButton size="small" onClick={resetForm} disabled={saving} aria-label="취소">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      )}

      {links.length === 0 && !loading && !adding ? (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ py: 1, color: 'text.secondary' }}
        >
          <Typography variant="body2">등록된 사이트 링크가 없습니다.</Typography>
          {canEdit && (
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAdding(true)}
              sx={{ textTransform: 'none' }}
            >
              추가하기
            </Button>
          )}
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {links.map((link) => {
            // 사이트마다 결정적 색상 + favicon(있으면) → 한눈에 구분되게.
            const color = colorFromSeed(link.name || link.url);
            return (
              <Chip
                key={link.id}
                avatar={
                  <Avatar
                    src={faviconUrl(link.url)}
                    sx={{ bgcolor: color, color: '#fff !important', fontSize: '0.72rem', fontWeight: 700 }}
                  >
                    {initialOf(link.name)}
                  </Avatar>
                }
                label={link.name}
                component="a"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                clickable
                variant="outlined"
                onDelete={canEdit ? () => handleDelete(link) : undefined}
                sx={{
                  maxWidth: 280,
                  fontWeight: 600,
                  borderColor: alpha(color, 0.5),
                  borderLeft: `4px solid ${color}`,
                  '&:hover': { bgcolor: alpha(color, 0.08), borderColor: color },
                }}
              />
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default SiteLinksBar;
